import { useFormContext } from 'react-hook-form';
import { Button, Icon, IconName, Stack, StackItem, Typography } from '@optimus-web/core';
import {
    FormCheckboxField,
    FormSelectField,
    FormSelectionCardGroupField,
    FormTextareaField,
    LocalizedText,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    UserGroup,
    PortfolioEnterpriseRelationshipResult,
    UserGroupAccessPolicyType,
    LearningPath,
} from '@mews-commander/data-access';
import { Loader, LocalizedLabel, LocalizedSubsectionTitle, NewTabLink, TooltippedIcon } from '@mews-commander/ui';
import { EnumSelectField } from '@mews-commander/form';
import { RolesField } from '@mews-commander/user-management/roles-field';
import { useEnterpriseOptions } from './useEnterpriseOptions';
import { UpdateGroupSettingsFormValues } from './useGroupSettingsForm';
import { getRolesAndPermissionsOptionsValues } from './utils';
import { useCashiersOptions } from './useCashiersOptions';

enum FormFields {
    Description = 'description',
    Enterprises = 'enterprises',
    RoleIds = 'rolesIds',
    AccessPolicyType = 'accessPolicyType',
    CashierExternalIdentifiers = 'cashierExternalIdentifiers',
    ReceiveCustomerMessages = 'receiveCustomerMessages',
    ResetUsersTwoFactorAuthentication = 'resetUsersTwoFactorAuthentication',
    AssignedLearningPath = 'assignedLearningPath',
}

const MAX_DESCRIPTION_LENGTH = 1000;

export const GroupSettingsForm = ({ group }: { group: UserGroup }) => {
    const { watch } = useFormContext<UpdateGroupSettingsFormValues>();

    const { options: cashierOptions, isLoading: isCashiersLoading } = useCashiersOptions(
        group.cashierExternalIdentifiers || []
    );

    const { onSearch, handleChange, handleLoadMoreClick, hasNextPage, isFetchingNextPage, selectOptions } =
        useEnterpriseOptions((group.enterprises || []) as Array<PortfolioEnterpriseRelationshipResult>);

    const rolesAndPermissionsOptions = getRolesAndPermissionsOptionsValues().map(
        ({ icon, label, value, description }) => ({
            media: <Icon value={icon} size="medium" />,
            label: (
                <Typography textStyle="bodySmallStrong" as="div">
                    <LocalizedText textKey={label} />
                </Typography>
            ),
            value,
            name: 'rolesAndPermissions',
            description: (
                <Typography textStyle="bodySmallStrong" variant="secondary" as="div">
                    <LocalizedText textKey={description} />
                </Typography>
            ),
            dataTestId: value,
        })
    );

    const groupValue = watch(FormFields.AccessPolicyType);
    const descriptionValue = watch(FormFields.Description) || '';
    const CASHIERS_SETTINGS_HELP_URL = 'https://help.mews.com/s/article/create-or-delete-a-cashier/';

    return (
        <Stack vertical spacing="600">
            <FormTextareaField
                name={FormFields.Description}
                label={<LocalizedText textKey={TextKey.GroupDescription} />}
                helper={
                    descriptionValue?.length > MAX_DESCRIPTION_LENGTH ? null : (
                        <LocalizedText
                            textKey={TextKey.UserGroupDescriptionMaxLength}
                            substitutes={{ number: MAX_DESCRIPTION_LENGTH }}
                        />
                    )
                }
            />
            <Stack vertical spacing="200">
                <LocalizedSubsectionTitle textKey={TextKey.Profile} />
                <EnumSelectField
                    label={<LocalizedText textKey={TextKey.LearningPath} />}
                    enumClass={LearningPath}
                    name={FormFields.AssignedLearningPath}
                    enumPrefix="LearningPath"
                    helper={<LocalizedText textKey={TextKey.LearningPathHelper} />}
                    showEmptyValue
                    size="medium"
                />
            </Stack>
            <FormSelectField
                name={FormFields.Enterprises}
                label={<LocalizedSubsectionTitle textKey={TextKey.Properties} />}
                size="medium"
                multiselect
                onSearchChange={onSearch}
                preserveMultiSelectSearch
                onChange={(_, values) => handleChange(Array.isArray(values) ? values : [values])}
                captionIconWithTooltip={
                    <TooltippedIcon
                        size="small"
                        value={IconName.Info}
                        tooltip={<LocalizedText textKey={TextKey.SelectPropertyHint} />}
                    />
                }
            >
                {selectOptions}

                {hasNextPage ? (
                    <StackItem padding="100">
                        <Button variant="ghost" onClick={handleLoadMoreClick} wide type="submit">
                            {isFetchingNextPage ? <Loader size="small" /> : <LocalizedText textKey={TextKey.More} />}
                        </Button>
                    </StackItem>
                ) : null}
            </FormSelectField>
            <Stack vertical spacing="300">
                <FormSelectionCardGroupField
                    label={
                        <Typography textStyle="titleSmallStrong" as="span">
                            <LocalizedText textKey={TextKey.RolesAndPermissions} />
                        </Typography>
                    }
                    required
                    type="radio"
                    name={FormFields.AccessPolicyType}
                    cardVariant="vertical"
                    options={rolesAndPermissionsOptions}
                    gap="100"
                    data-test-id="roles"
                    captionIconWithTooltip={
                        <TooltippedIcon
                            size="small"
                            value={IconName.Info}
                            tooltip={<LocalizedText textKey={TextKey.RolesAndPermissionsHint} />}
                        />
                    }
                />

                {groupValue === UserGroupAccessPolicyType.RoleBased ? <RolesField /> : null}

                {groupValue !== UserGroupAccessPolicyType.NoAccess && (
                    <div>
                        <LocalizedLabel textKey={TextKey.AdvancedSettings} as="div" />
                        <FormCheckboxField name={FormFields.ReceiveCustomerMessages} size="small">
                            <LocalizedText textKey={TextKey.ReceiveCustomerMessages} />
                        </FormCheckboxField>

                        {groupValue === UserGroupAccessPolicyType.Admin && (
                            <FormCheckboxField name={FormFields.ResetUsersTwoFactorAuthentication} size="small">
                                <LocalizedText textKey={TextKey.ResetTwoFactor} />
                            </FormCheckboxField>
                        )}
                    </div>
                )}

                <FormSelectField
                    name={FormFields.CashierExternalIdentifiers}
                    label={<LocalizedText textKey={TextKey.CashierExternalIdentifiers} />}
                    loader={isCashiersLoading}
                    size="medium"
                    multiselect
                    helper={
                        <LocalizedText
                            textKey={TextKey.CashierExternalIdentifiersHelper}
                            substitutes={{
                                cashierSettingsLink: (
                                    <NewTabLink url={CASHIERS_SETTINGS_HELP_URL}>
                                        <LocalizedText textKey={TextKey.CashierSettings} />
                                    </NewTabLink>
                                ),
                            }}
                        />
                    }
                >
                    {cashierOptions}
                </FormSelectField>
            </Stack>
        </Stack>
    );
};
